import type {
  AnswerRequestBody,
  DeliveryRequest,
  EditShipmentBody,
  ImportPreview,
  ImportPushBody,
  ImportResult,
  MarkShippedBody,
  PackingList,
  PlanShipmentBody,
  PoDetail,
  PoTrackerList,
  PoTrackerQuery,
  RequestDeliveryBody,
  Shipment,
  ShippingEvent,
  TrackerUpdates,
} from '@/types/poTracker';
import apiClient from './axios';

const BASE = '/po-tracker';
const poPath = (poNumber: string): string => `${BASE}/pos/${encodeURIComponent(poNumber)}`;
const INLINE = { skipGlobalError: true } as const;

export async function fetchTrackerList(q: PoTrackerQuery): Promise<PoTrackerList> {
  const { data } = await apiClient.get<PoTrackerList>(BASE, {
    params: { vendorId: q.vendorId, tab: q.tab, search: q.search?.trim() || undefined, page: q.page },
    // The page shows a failed load in its own banner with Retry.
    ...INLINE,
  });
  return data;
}

// inline: the caller shows a refusal next to its own field instead of a toast.
export async function fetchPoDetail(vendorId: string, poNumber: string, inline = false): Promise<PoDetail> {
  const { data } = await apiClient.get<PoDetail>(poPath(poNumber), { params: { vendorId }, ...(inline ? INLINE : {}) });
  return data;
}

export async function fetchTrackerUpdates(vendorId: string): Promise<TrackerUpdates> {
  const { data } = await apiClient.get<TrackerUpdates>(`${BASE}/updates`, { params: { vendorId } });
  return data;
}

export async function markTrackerSeen(vendorId: string): Promise<{ seenAt: string }> {
  const { data } = await apiClient.post<{ seenAt: string }>(`${BASE}/seen`, { vendorId });
  return data;
}

export async function planShipment(poNumber: string, body: PlanShipmentBody): Promise<Shipment> {
  const { data } = await apiClient.post<Shipment>(`${poPath(poNumber)}/shipments`, body, INLINE);
  return data;
}

export async function editShipment(id: string, body: EditShipmentBody): Promise<Shipment> {
  const { data } = await apiClient.patch<Shipment>(`${BASE}/shipments/${id}`, body, INLINE);
  return data;
}

export async function removeShipment(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/shipments/${id}`, INLINE);
}

export async function markShipped(body: MarkShippedBody): Promise<ShippingEvent> {
  const { data } = await apiClient.post<ShippingEvent>(`${BASE}/shipping-events`, body, INLINE);
  return data;
}

export async function setHawb(eventId: string, hawb: string): Promise<ShippingEvent> {
  const { data } = await apiClient.patch<ShippingEvent>(`${BASE}/shipping-events/${eventId}`, { hawb }, INLINE);
  return data;
}

export async function uploadPackingList(eventId: string, file: File, note: string | null): Promise<PackingList> {
  const formData = new FormData();
  formData.append('file', file);
  if (note) formData.append('note', note);
  const { data } = await apiClient.post<PackingList>(`${BASE}/shipping-events/${eventId}/packing-lists`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    ...INLINE,
  });
  return data;
}

export async function fetchPackingList(id: string): Promise<PackingList> {
  const { data } = await apiClient.get<PackingList>(`${BASE}/packing-lists/${id}`, INLINE);
  return data;
}

export async function packingListDownloadUrl(id: string): Promise<string> {
  const { data } = await apiClient.get<{ url: string }>(`${BASE}/packing-lists/${id}/download`, INLINE);
  return data.url;
}

export async function requestDelivery(poNumber: string, body: RequestDeliveryBody): Promise<DeliveryRequest> {
  const { data } = await apiClient.post<DeliveryRequest>(`${poPath(poNumber)}/delivery-requests`, body, INLINE);
  return data;
}

export async function withdrawRequest(id: string): Promise<DeliveryRequest> {
  const { data } = await apiClient.post<DeliveryRequest>(`${BASE}/delivery-requests/${id}/withdraw`, undefined, INLINE);
  return data;
}

export async function answerRequest(id: string, body: AnswerRequestBody): Promise<DeliveryRequest> {
  const { data } = await apiClient.post<DeliveryRequest>(`${BASE}/delivery-requests/${id}/answer`, body, INLINE);
  return data;
}

export async function previewImport(vendorId: string, file: File): Promise<ImportPreview> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('vendorId', vendorId);
  const { data } = await apiClient.post<ImportPreview>(`${BASE}/import/preview`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    ...INLINE,
  });
  return data;
}

export async function pushImport(body: ImportPushBody): Promise<ImportResult> {
  const { data } = await apiClient.post<ImportResult>(`${BASE}/import`, body, INLINE);
  return data;
}
